import '@workspace/ui/globals.css';

import { init } from '@module-federation/enhanced/runtime';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { TouchProvider } from '@workspace/ui/components/hybrid-tooltip';
import { Toaster } from '@workspace/ui/components/sonner';
import { Spinner } from '@workspace/ui/components/spinner';
import { ThemeProvider } from '@workspace/ui/components/theme-provider';
import React from 'react';
import ReactDOM from 'react-dom/client';

import { type AuthContextProps, AuthProvider } from '~/auth/auth-context';
import { useAuth } from '~/auth/use-auth';
import { NotFound } from '~/components/not-found';
import { SENTRY_DSN } from '~/config/environment';
import { initBloque } from '~/libs/bloque';
import { m } from '~/paraglide/messages.js';
import { remotes } from '~/remotes';
import { GlobalErrorHandler } from './components/global-error-boundary';
import { OrganizationProvider } from './context/organization/org-context';
import { SocketProvider } from './context/socket/socket-context';

// Import the generated route tree
import { routeTree } from './routeTree.gen';

Sentry.init({
  dsn: SENTRY_DSN,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
});

init(remotes);

const queryClient = new QueryClient();

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: undefined as unknown as {
    auth: AuthContextProps;
  },
  defaultNotFoundComponent: NotFound,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function App() {
  const auth = useAuth();
  const [isBloqueReady, setIsBloqueReady] = React.useState(false);

  React.useEffect(() => {
    if (!auth.isAuthenticated) {
      setIsBloqueReady(true);
      return;
    }

    let isMounted = true;
    setIsBloqueReady(false);
    initBloque()
      .then(() => {
        if (isMounted) {
          setIsBloqueReady(true);
        }
      })
      .catch((error) => {
        console.error('Failed to initialize Bloque SDK', error);
        if (isMounted) {
          setIsBloqueReady(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [auth.isAuthenticated]);

  if (auth.loading) return <Spinner message={m['common.thinking']()} />;
  if (!isBloqueReady) return <Spinner message={m['common.thinking']()} />;

  return <RouterProvider router={router} context={{ auth }} />;
}

function AuthCallbackHandler() {
  return (
    <AuthProvider>
      <SocketProvider>
        <OrganizationProvider>
          <App />
          <Toaster richColors closeButton expand />
          <ReactQueryDevtools initialIsOpen={false} />
        </OrganizationProvider>
      </SocketProvider>
    </AuthProvider>
  );
}

const rootEl = document.getElementById('root');
if (rootEl) {
  const root = ReactDOM.createRoot(rootEl);
  root.render(
    <React.StrictMode>
      <GlobalErrorHandler fallback={<h2>Ups, algo se rompió 😅</h2>}>
        <TouchProvider>
          <ThemeProvider defaultTheme="dark" storageKey="ui-theme">
            <QueryClientProvider client={queryClient}>
              <AuthCallbackHandler />
            </QueryClientProvider>
          </ThemeProvider>
        </TouchProvider>
      </GlobalErrorHandler>
    </React.StrictMode>,
  );
}
